import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Sparkles, Star, Check, Target, X } from "lucide-react";
import type { KeywordSuggestion } from "./KeywordResearch";

const MARKETS = [
  { code: "MA", flag: "🇲🇦", label: "Maroc" },
  { code: "FR", flag: "🇫🇷", label: "France" },
  { code: "ES", flag: "🇪🇸", label: "Espagne" },
  { code: "US", flag: "🇺🇸", label: "États-Unis" },
  { code: "BE", flag: "🇧🇪", label: "Belgique" },
];

const KeywordCompetitor = ({ onConfirm }: { onConfirm: (k: KeywordSuggestion) => void }) => {
  const [theme, setTheme] = useState("transferts privés, transport touristique");
  const [market, setMarket] = useState("FR");
  const [items, setItems] = useState<KeywordSuggestion[]>([]);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("seo-keyword-research", {
        body: { topic: theme, mode: "competitor", competitor: "maroctransfers.com", market },
      });
      if (error) throw error;
      if ((data as any)?.fallback) {
        toast.warning((data as any)?.reason || "Service IA indisponible.");
        return;
      }
      const list = (data as any)?.keywords as KeywordSuggestion[] | undefined;
      if (!list?.length) throw new Error("Aucun mot-clé renvoyé");
      setItems(list);
      toast.success(`${list.length} mots-clés concurrents (${market})`);
    } catch (e: any) {
      toast.error(e?.message || "Échec de l'analyse");
    } finally { setLoading(false); }
  };

  const favorite = async (k: KeywordSuggestion) => {
    const { error } = await (supabase as any).from("blog_keyword_favorites").insert({
      keyword: k.keyword, intent: k.intent || "", volume_estimate: k.volume_estimate || "",
      difficulty: k.difficulty || "", notes: `[${market}] ${k.rationale || ""}`,
    });
    if (error) toast.error(error.message); else toast.success("Ajouté aux favoris");
  };

  const remove = (idx: number) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const confirm = async (k: KeywordSuggestion, idx: number) => {
    const { error } = await (supabase as any)
      .from("blog_keyword_queue")
      .insert({ keyword: k.keyword, status: "pending" });
    if (error && !/duplicate|unique/i.test(error.message)) {
      toast.error(error.message);
      return;
    }
    window.dispatchEvent(new CustomEvent("blog-keyword-queue-changed"));
    onConfirm(k);
    remove(idx);
    toast.success("Mot-clé envoyé au tableau du Guide");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" /> Mots-clés concurrents — cible : maroctransfers.com
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={theme} onChange={(e) => setTheme(e.target.value)} placeholder="Thématique (transport, transferts, excursions…)" maxLength={200} />
          <Select value={market} onValueChange={setMarket}>
            <SelectTrigger className="w-full sm:w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MARKETS.map((m) => (
                <SelectItem key={m.code} value={m.code}>{m.flag} {m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={run} disabled={loading} className="whitespace-nowrap">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            Analyser
          </Button>
        </div>
        {items.length > 0 && (
          <div className="rounded-md border border-border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mot-clé</TableHead>
                  <TableHead>Intention</TableHead>
                  <TableHead>Volume</TableHead>
                  <TableHead>Difficulté</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((k, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">
                      {k.keyword}
                      {k.rationale && <p className="text-xs text-muted-foreground mt-1">{k.rationale}</p>}
                    </TableCell>
                    <TableCell><Badge variant="secondary">{k.intent}</Badge></TableCell>
                    <TableCell><Badge variant="outline">{k.volume_estimate}</Badge></TableCell>
                    <TableCell><Badge variant="outline">{k.difficulty}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" onClick={() => confirm(k, i)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                          <Check className="h-4 w-4 mr-1" />Confirmer
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => remove(i)} title="Retirer">
                          <X className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => favorite(k)} title="Ajouter aux favoris">
                          <Star className="h-4 w-4 text-amber-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default KeywordCompetitor;